import { UserReference } from '../admin/admin.types';
import { DateRangeValueType, TDateRangeValueType } from '../date-range/date-range.enums';
import { TOfferKind, TOfferStatus } from '../offer/offer.types';
import { BillingPlanTier } from '../products/products.types';
import { FullSearchCandidateRecord } from '../search/search.types';
import { Visibility } from '../visibility/visibility.types';

// new

export interface ReportDashboard {
  id: any;
  name: string;
  filters: ReportParamFilter;
  dateFilter: ReportDateFilter;
  components: ReportDashboardComponent[];
}

export type ReportParamFilter = { group: string; value: string }[];

export enum ReportDashboardComponentChartType {
  Bar = 'bar',
  Line = 'line',
  Pie = 'pie',
}

export enum ReportDashboardComponentKind {
  Trend = 'trend',
  Breakdown = 'breakdown',
  SingleMetric = 'single_metric',
  Funnel = 'funnel',
}

export enum ReportDashboardComponentViewMode {
  Chart = 'chart',
  Summary = 'summary',
  Table = 'table',
}

export enum ReportAxis {
  X = 'x',
  Y = 'y',
}

export enum TypeFilter {
  All = 'all',
  Charts = 'chart',
  Tables = 'table',
  Summary = 'summary',
  Breakdown = 'breakdown',
  Single = 'single_metric',
  Trend = 'trend',
  Pipeline = 'funnel',
}

export enum ReportResourceType {
  Candidates = 'candidates',
  Evaluations = 'evaluations',
  Events = 'events',
  Jobs = 'jobs',
}

export type ReportResourceTypeUnion = ReportResourceType.Candidates &
  ReportResourceType.Evaluations &
  ReportResourceType.Events &
  ReportResourceType.Jobs;

export enum ReportLibraryComponentCategory {
  All = 'All',
  Candidates = 'candidates',
  Jobs = 'jobs',
  Disqualifications = 'disqualifications',
  TeamMembers = 'team_members',
  TalentPools = 'talent_pools',
  Evaluations = 'evaluations',
  Events = 'events',
  Hires = 'hires',
  Pipelines = 'pipelines',
}

export type ReportDateFilter =
  | {
      dateRange: DateRangeValueType;
      dateStart: null;
      dateEnd: null;
    }
  | {
      dateRange: 'range';
      dateStart: string;
      dateEnd: string;
    }
  | {
      dateRange: null;
      dateStart: null;
      dateEnd: null;
    };

export interface ReportDashboardComponent {
  id: string | number | null;
  localId?: string | null;
  readOnly?: boolean;
  name: string;
  description: string;
  chartType: ReportDashboardComponentChartType | null;
  metric: string;
  kind: ReportDashboardComponentKind;
  grid: {
    cols: number;
    rows: number;
    x: number;
    y: number;
  };
  sortBy: string | null;
  sortOrder: 'asc' | 'desc' | null;
  settings?: {
    interval?: 'daily' | 'weekly' | 'monthly' | 'quarterly';
    amountOfDataDisplayed?: number;
    localSortBy?: string | null;
    localSortOrder?: 'asc' | 'desc' | null;
  };
  filters: ReportParamFilter;
  primaryGroup: string | null;
  secondaryGroup: string | null;
  dateFilter: ReportDateFilter;
  dateField: {
    field: string;
    resource: string;
  };
  excludeDeletedCandidates?: boolean;
  viewMode: ReportDashboardComponentViewMode;
}

export interface AvailableDateField {
  field: string;
  resource: string;
  isDefault?: boolean;
}

// metric
export interface ReportDashboardBuilderMetricOptions {
  availableChartTypes: string[];
  availableDateFields: AvailableDateField[];
  availableDateFilters: DateRangeValueType[];
  availableFilters: string[];
  availableFromTier: BillingPlanTier;
  availableFunctions: string[];
  availableGroups: Record<string, string[]>;
  availableOptions?: string[];
  availableSortOptions: string[];
  availableViewModes: string[];
  isNew: boolean;
  isAvailable: boolean;
  isSortable: boolean;
  kind: ReportDashboardComponentKind;
  metric: string;
  name: string;
  unitType: 'number' | 'duration' | null;
  yAxis: string;
  xAxis: string;
  tooltip: string;
  resource: ReportResourceType;
  defaultGroup: string;
}

export interface ReportDashboardLibraryComponent extends ReportDashboardComponent {
  isAvailable: boolean;
  isNew: boolean;
  categories: ReportLibraryComponentCategory[];
  libraryHeader: string;
  libraryDescription: string;
  sampleData: ReportDashboardComponentDataResponse['results'];
}

export interface ReportDashboardReadOnlyComponent extends ReportDashboardComponent {
  id: string;
  localId: string;
  readOnly: true;
  primaryGroup: string;
  secondaryGroup: string;
}

export interface ReportDashboardEditableComponent extends ReportDashboardComponent {
  id: number;
  localId: string;
  primaryGroup: string;
  secondaryGroup: string;
}

export type KnownDashboardIds =
  | 'candidate_overview'
  | 'disqualifications'
  | 'job_overview'
  | 'events_and_evaluations';

export interface ReportDashboardReadOnly extends ReportDashboard {
  id: string;
  components: ReportDashboardReadOnlyComponent[];
  description: string;
  readOnly: true;
}

export interface ReportDashboardEditable extends ReportDashboard {
  id: number;
  components: ReportDashboardEditableComponent[];
  visibility: Visibility;
  position: number;
}

export interface ReportDashboardsResponse {
  dashboards: ReportDashboardEditable[];
  standardDashboards: ReportDashboardReadOnly[];
}

export interface ReportDashboardLibraryResponse {
  componentTemplates: ReportDashboardLibraryComponent[];
  metrics: ReportDashboardBuilderMetricOptions[];
}

export interface ReportDashboardComponentDataResponse {
  checksum: string;
  results: {
    columns: { name: string; id: number }[];
    rows: {
      category?: string;
      name: string;
      id: string | number;
      values: (number | number[])[];
    }[];

    // TODO: figure out typings here (metric is merged into meta object for data visualization, it is not part of data response)
    meta: Record<string, any>;
  };
}

export type ReportDashboardUnion =
  | ReportDashboard
  | ReportDashboardEditable
  | ReportDashboardReadOnly;

export type ReportDashboardComponentUnion =
  | ReportDashboardComponent
  | ReportDashboardLibraryComponent
  | ReportDashboardEditableComponent
  | ReportDashboardReadOnlyComponent;

export type ReportDashboardScope = keyof ReportDashboardsResponse;

export interface ReportEventsDetailRecord {
  id: number;
  adminName: string | null;
  adminAvatarUrl: string | null;
  candidateAvatarUrl: string;
  candidateName: string;
  candidateId: number;
  duration: number; // minutes
  kind: 'meeting' | 'onsite' | 'call';
  location: string;
  note: string | null;
  publicNote: string | null;
  startsAt: number;
  offerId: string;
  offerKind: string;
  offerTitle: string;
  offerSlug: string;
  timezone: string;
}

export interface ReportEvaluationDetailsRecord {
  id: number;
  adminName: string | null;
  adminAvatarUrl: string | null;
  candidateId: number;
  candidateName: string;
  candidateAvatarUrl: string;
  departmentName: string;
  guestName: string;
  offerId: number;
  offerKind: TOfferKind;
  offerStatus: TOfferStatus;
  offerTitle: string;
  offerSlug: string;
  rating: 'strong_no' | 'no' | 'neutral' | 'yes' | 'strong_yes';
  ratingNote: string;
  evaluationForm: string;
}

// TODO: reuse types from offers ?
export interface ReportJobDetailsRecord {
  id: number;
  slug: number;
  candidatesCount: number;
  department: string | null;
  languages: string;
  location: string;
  status: TOfferStatus;
  tags: string;
  title: string;
}

export interface ReportEventsDetailsResponse {
  results: ReportEvaluationDetailsRecord[];
}

export interface ReportEvaluationsDetailsResponse {
  results: ReportEventsDetailRecord[];
}

export interface ReportCandidatesDetailsResponse {
  results: FullSearchCandidateRecord[];
}

export interface ReportJobsDetailsResponse {
  results: ReportJobDetailsRecord[];
}

export type ReportDetailsRecordUnion =
  | ReportEventsDetailRecord
  | ReportEvaluationDetailsRecord
  | FullSearchCandidateRecord
  | ReportJobDetailsRecord;

// legacy
export type ReportKind = 'company' | 'personal';
export type ReportType =
  | 'candidates.quality_of_candidates'
  | 'candidates.time_to_hire'
  | 'jobs.dropoff_rate'
  | 'jobs.pipeline_speed'
  | 'jobs.proceed_rate';

export type ReportDateRange = 'range' | TDateRangeValueType;

export interface Segment {
  admin?: UserReference;
  adminId: number;
  dateEnd: string;
  dateRange: ReportDateRange;
  dateStart: string;
  filters: string;
  groupBy: string;
  id: number;
  kind: ReportKind;
  name: 'string';
  position: number;
  reportType: ReportType;
  sortBy: string;
  sortOrder: 'asc' | 'desc' | null;
}

export type ReportFilter = any;
export type Report = {
  addedCount?: number;
  appliedAvg?: number;
  appliedCount?: number;
  appliedMax?: number;
  appliedMin?: number;
  avgClosedTime?: number;
  avgDraftTime?: number;
  avgInternalTime?: number;
  avgMinutes?: number;
  avgPublishedTime?: number;
  avgTime?: number;
  avgTimeToReply?: number;
  calendarEventsCount?: number;
  closedCount?: number;
  columns: string[];
  completedCount?: number;
  completedEvaluationsCount?: number;
  completedTasksCount?: number;
  contactedCount?: number;
  disqualifiedCount?: number;
  draftCount?: number;
  emailCount?: number;
  emailReplies?: number;
  emailSentCount?: number;
  evaluatedAvg?: number;
  evaluatedCount?: number;
  evaluatedMax?: number;
  evaluatedMin?: number;
  evaluationCount?: number;
  evaluationTime?: number;
  evaluationsCount?: number;
  hireCount?: number;
  hireTime?: number;
  hiredCount?: number;
  internalCount?: number;
  interviewCount?: number;
  interviewTime?: number;
  interviewedAvg?: number;
  interviewedCount?: number;
  interviewedMax?: number;
  interviewedMin?: number;
  maxMinutes?: number;
  meetingCount?: number;
  minMinutes?: number;
  movedForwardCount?: number;
  noCount?: number;
  notCompletedCount?: number;
  notContactedCount?: number;
  notEvaluatedCount?: number;
  notSureCount?: number;
  notesCount?: number;
  offerCount?: number;
  offerIds?: number[];
  offerTime?: number;
  offeredAvg?: number;
  offeredCount?: number;
  offeredMax?: number;
  offeredMin?: number;
  onSiteCount?: number;
  phoneCount?: number;
  phoneScreenCount?: number;
  phoneScreenTime?: number;
  phoneScreenedAvg?: number;
  phoneScreenedCount?: number;
  phoneScreenedMax?: number;
  phoneScreenedMin?: number;
  publishedCount?: number;
  receivedEmailCount?: number;
  requestedEvaluationsCount?: number;
  sentEmailCount?: number;
  sourcedAvg?: number;
  sourcedCount?: number;
  sourcedMax?: number;
  sourcedMin?: number;
  strongYesCount?: number;
  totalActivitiesCount?: number;
  totalCandidatesCount?: number;
  totalCount: number;
  totalEmailCount?: number;
  totalEvaluationsCount?: number;
  totalTasksCount?: number;
  visitsCount?: number;
  yesCount?: number;
  values: [any];
};
